import { SearchRounded } from '@mui/icons-material';
import {
  Checkbox,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import { useState } from 'react';

export interface ListboxProps<T> {
  options: readonly T[];
  getOptionKey?: (option: T) => string;
  getOptionLabel?: (option: T) => string | ListItemLabel;

  selectedKeys: ReadonlySet<string>;
  onChange(key: string, selected: boolean): void;

  hideSearch?: boolean;
  emptyState?: string;
  onInputTextChange?: (value: string) => void;
}

export interface ListItemLabel {
  primary: string;
  secondary?: string;
}

export function Listbox<T>({
  options,
  getOptionKey = toString,
  getOptionLabel = toString,
  selectedKeys,
  onChange,
  emptyState,
  hideSearch,
  onInputTextChange,
}: ListboxProps<T>) {
  const [searchText, setSearchText] = useState('');

  const lcSearchText = searchText.toLocaleLowerCase();

  // NOTE: only if onInputTextChange not given, do client side filtering
  const matchingOptions =
    searchText && !onInputTextChange
      ? options.filter((option) => {
          const label = getOptionLabel(option);
          if (typeof label === 'string') {
            return label.toLocaleLowerCase().includes(lcSearchText);
          } else {
            return (
              label.primary.toLocaleLowerCase().includes(lcSearchText) ||
              label.secondary?.toLocaleLowerCase().includes(lcSearchText)
            );
          }
        })
      : options;

  return (
    <Root>
      {!hideSearch && (
        <SearchInputContainer>
          <SearchRounded sx={{ color: 'neutral.main' }} />
          <Input
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (onInputTextChange) {
                onInputTextChange(e.target.value);
              }
            }}
            sx={{ flex: '1 0 0' }}
          />
        </SearchInputContainer>
      )}
      <List>
        {matchingOptions.map((option) => {
          const key = getOptionKey(option);
          const label = getOptionLabel(option);
          const isSelected = selectedKeys.has(key);

          return (
            <ListItem key={key} disablePadding>
              <ListItemButton onClick={() => onChange(key, !isSelected)} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                {typeof label === 'string' ? (
                  <ListItemText primary={label} />
                ) : (
                  <ListItemText {...label} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
        {!matchingOptions.length && !!emptyState && (
          <ListItem>
            <ListItemText>{emptyState}</ListItemText>
          </ListItem>
        )}
      </List>
    </Root>
  );
}

const Root = styled('div')({
  width: 240,
  maxHeight: 'min(600px, 80vh)',
});

const SearchInputContainer = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

function toString(option: unknown): string {
  return `${option}`;
}
